import moment from 'moment'

/** Default pipeline metrics for week-over-week comparison (matches Status Wise Tracker summary columns). */
export const COMPARISON_METRICS = [
  { key: 'TotalCandidates', label: 'Total Candidates' },
  { key: 'ProfileShared', label: 'Profile Shared' },
  { key: 'Interview', label: 'Interview' },
  { key: 'Shortlisted', label: 'Shortlisted' },
  { key: 'Offered', label: 'Offered' },
  { key: 'NotOfferedDrop', label: 'Not Offered / Drop' },
  { key: 'Joined', label: 'Joined' },
  { key: 'Revenue', label: 'Revenue' },
]

/**
 * Mon–Sat week range (same logic as StatusWiseTracker default dates).
 * @param {import('moment').MomentInput} [referenceDate]
 */
export function getMonSatWeekRange(referenceDate) {
  const today = moment(referenceDate).locale('en')
  const start = today.clone().startOf('week').add(1, 'days')
  const end = today.clone().endOf('week')
  return {
    start: start.format('YYYY-MM-DD'),
    end: end.format('YYYY-MM-DD'),
  }
}

/** Previous calendar week with the same Mon–Sat span. */
export function getPreviousWeekRange(startDate, endDate) {
  return {
    start: moment(startDate).subtract(7, 'days').format('YYYY-MM-DD'),
    end: moment(endDate).subtract(7, 'days').format('YYYY-MM-DD'),
  }
}

function toNumber(value) {
  if (value == null || value === '' || value === '—') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function finalizeMetricFlags(bucket) {
  COMPARISON_METRICS.forEach(m => {
    if (!bucket[`${m.key}__has`]) {
      bucket[m.key] = null
    }
    delete bucket[`${m.key}__has`]
  })
}

function initMetricBucket(extra = {}) {
  const bucket = { ...extra }
  COMPARISON_METRICS.forEach(m => {
    bucket[m.key] = 0
    bucket[`${m.key}__has`] = false
  })
  return bucket
}

function addMetricsToBucket(bucket, row, getMetricValue) {
  COMPARISON_METRICS.forEach(m => {
    const val = getMetricValue(row, m.key)
    if (val != null) {
      bucket[m.key] += val
      bucket[`${m.key}__has`] = true
    }
  })
}

/**
 * @param {object[]} mappedRows - rows after mapRow()
 * @param {string} metricKey
 * @param {(row: object, key: string) => number|null} getMetricValue
 */
export function sumMetric(mappedRows, metricKey, getMetricValue) {
  if (!Array.isArray(mappedRows) || !mappedRows.length) return 0
  let total = 0
  let hasNumeric = false
  mappedRows.forEach(row => {
    const val = getMetricValue(row, metricKey)
    if (val != null) {
      total += val
      hasNumeric = true
    }
  })
  return hasNumeric ? total : null
}

/**
 * @param {object[]} mappedRows
 * @param {(row: object) => string} getClientLabel
 * @param {(row: object, key: string) => number|null} getMetricValue
 */
export function aggregateByClient(mappedRows, getClientLabel, getMetricValue) {
  const byClient = {}
  ;(mappedRows || []).forEach(row => {
    const client = getClientLabel(row) || 'Unknown'
    if (!byClient[client]) {
      byClient[client] = initMetricBucket({ client })
    }
    addMetricsToBucket(byClient[client], row, getMetricValue)
  })
  Object.keys(byClient).forEach(client => {
    finalizeMetricFlags(byClient[client])
  })
  return byClient
}

/**
 * Aggregate metrics by client + position (for position-wise week comparison).
 * @param {object[]} mappedRows
 * @param {(row: object) => string} getClientLabel
 * @param {(row: object) => string} getPositionLabel
 * @param {(row: object, key: string) => number|null} getMetricValue
 */
export function aggregateByClientPosition(mappedRows, getClientLabel, getPositionLabel, getMetricValue) {
  const byGroup = {}
  ;(mappedRows || []).forEach(row => {
    const client = getClientLabel(row) || 'Unknown'
    const position = getPositionLabel(row) || '—'
    const key = `${client}\u0000${position}`
    if (!byGroup[key]) {
      byGroup[key] = initMetricBucket({ client, position })
    }
    addMetricsToBucket(byGroup[key], row, getMetricValue)
  })
  Object.keys(byGroup).forEach(key => {
    finalizeMetricFlags(byGroup[key])
  })
  return byGroup
}

export function aggregateOverall(mappedRows, getMetricValue) {
  const totals = {}
  COMPARISON_METRICS.forEach(m => {
    totals[m.key] = sumMetric(mappedRows, m.key, getMetricValue)
  })
  return totals
}

export function computeDelta(current, previous) {
  const cur = toNumber(current)
  const prev = toNumber(previous)
  if (cur == null && prev == null) {
    return { change: null, changePct: null }
  }
  const c = cur ?? 0
  const p = prev ?? 0
  const change = c - p
  let changePct = null
  if (p !== 0) {
    changePct = (change / p) * 100
  } else if (c !== 0) {
    changePct = 100
  } else {
    changePct = 0
  }
  return { change, changePct }
}

/**
 * Build flat table rows for overall comparison.
 */
export function buildOverallComparisonRows(currentTotals, previousTotals) {
  return COMPARISON_METRICS.map(metric => {
    const current = currentTotals[metric.key]
    const previous = previousTotals[metric.key]
    const { change, changePct } = computeDelta(current, previous)
    return {
      groupKey: '__overall__',
      groupLabel: '',
      metricKey: metric.key,
      metricLabel: metric.label,
      current,
      previous,
      change,
      changePct,
    }
  })
}

/**
 * Build flat table rows grouped by client (one row per client × metric).
 * @deprecated Prefer buildWeekWiseComparisonRows for By client week comparison.
 */
export function buildClientComparisonRows(currentByClient, previousByClient) {
  const clients = [...new Set([
    ...Object.keys(currentByClient || {}),
    ...Object.keys(previousByClient || {}),
  ])].sort((a, b) => a.localeCompare(b))

  const rows = []
  clients.forEach(client => {
    const cur = currentByClient[client] || {}
    const prev = previousByClient[client] || {}
    COMPARISON_METRICS.forEach(metric => {
      const current = cur[metric.key] ?? null
      const previous = prev[metric.key] ?? null
      const { change, changePct } = computeDelta(current, previous)
      rows.push({
        groupKey: client,
        groupLabel: client,
        metricKey: metric.key,
        metricLabel: metric.label,
        current,
        previous,
        change,
        changePct,
      })
    })
  })
  return rows
}

function sortGroupKeys(keys, currentByGroup, previousByGroup, includePosition) {
  return [...keys].sort((a, b) => {
    const aMeta = currentByGroup[a] || previousByGroup[a] || {}
    const bMeta = currentByGroup[b] || previousByGroup[b] || {}
    const clientCmp = String(aMeta.client || a).localeCompare(String(bMeta.client || b))
    if (clientCmp !== 0 || !includePosition) return clientCmp
    return String(aMeta.position || '').localeCompare(String(bMeta.position || ''))
  })
}

/**
 * Period label for week-comparison rows: `10-AUG to 15-AUG-2026`.
 * @param {import('moment').MomentInput} start
 * @param {import('moment').MomentInput} end
 */
export function formatComparisonPeriodRange(start, end) {
  if (!start || !end) return '—'
  const startPart = moment(start).locale('en').format('DD-MMM').toUpperCase()
  const endPart = moment(end).locale('en').format('DD-MMM-YYYY').toUpperCase()
  return `${startPart} to ${endPart}`
}

/**
 * Week-wise transposed rows for By client comparison.
 * Layout: Client | [Position] | Week | metric columns…
 * Each group emits two period rows; Δ / Δ% live on the current (newer) row for inline display/export.
 *
 * @param {Record<string, object>} currentByGroup - from aggregateByClient or aggregateByClientPosition
 * @param {Record<string, object>} previousByGroup
 * @param {{ includePosition?: boolean, currentWeekLabel?: string, previousWeekLabel?: string }} [options]
 */
export function buildWeekWiseComparisonRows(currentByGroup, previousByGroup, options = {}) {
  const {
    includePosition = false,
    currentWeekLabel = 'This week',
    previousWeekLabel = 'Last week',
  } = options

  const keys = sortGroupKeys(
    [...new Set([
      ...Object.keys(currentByGroup || {}),
      ...Object.keys(previousByGroup || {}),
    ])],
    currentByGroup || {},
    previousByGroup || {},
    includePosition,
  )

  const rows = []
  keys.forEach(key => {
    const cur = currentByGroup[key] || {}
    const prev = previousByGroup[key] || {}
    const client = cur.client || prev.client || key
    const position = cur.position || prev.position || '—'

    const base = {
      groupKey: key,
      groupLabel: client,
    }
    if (includePosition) {
      base.positionLabel = position
    }

    const currentRow = { ...base, weekLabel: currentWeekLabel, rowType: 'current' }
    const previousRow = { ...base, weekLabel: previousWeekLabel, rowType: 'previous' }

    COMPARISON_METRICS.forEach(metric => {
      const current = cur[metric.key] ?? null
      const previous = prev[metric.key] ?? null
      const { change, changePct } = computeDelta(current, previous)
      currentRow[metric.key] = current
      previousRow[metric.key] = previous
      currentRow[`${metric.key}__change`] = change
      currentRow[`${metric.key}__changePct`] = changePct
    })

    rows.push(currentRow, previousRow)
  })
  return rows
}

export function formatComparisonValue(value) {
  if (value == null) return '—'
  return value
}

export function formatDeltaValue(change) {
  if (change == null) return '—'
  if (change > 0) return `+${change}`
  return String(change)
}

export function formatDeltaPct(changePct) {
  if (changePct == null) return '—'
  const rounded = Math.round(changePct * 10) / 10
  if (rounded > 0) return `+${rounded}%`
  return `${rounded}%`
}

/** Inline secondary delta under a period value, e.g. `-93 (-85.3%)`. */
export function formatInlineDelta(change, changePct) {
  if (change == null && changePct == null) return ''
  return `${formatDeltaValue(change)} (${formatDeltaPct(changePct)})`
}

export function deltaVariant(change, higherIsBetter = true) {
  if (change == null || change === 0) return 'secondary'
  const improved = higherIsBetter ? change > 0 : change < 0
  return improved ? 'success' : 'danger'
}
